// src/features/links/MultiSelectFilter.tsx
// IR-1047 Phase 3.5 — OPS filter convention (Rotem): a labeled dropdown with
// multi-select checkboxes and inner Clear / Apply. Selections are DRAFT until
// Apply is clicked (apply-on-Apply), so ticking several boxes fires one refetch.
// Reusable across ops tabs; wired into the Join Links filter bar here.
import { useEffect, useRef, useState } from "react";

export interface MultiSelectOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: MultiSelectOption[];
  selected: string[];
  onApply: (next: string[]) => void;
  /** IR-1047: show a type-to-filter box above the list — for large option sets
   *  (e.g. the "Group" filter). Defaults off so existing filters are unchanged. */
  searchable?: boolean;
}

export function MultiSelectFilter({ label, options, selected, onApply, searchable }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  // Keep the draft in sync with the committed selection while closed (so an
  // external Reset reflects here, and re-opening starts from the live value).
  useEffect(() => {
    if (!open) {
      setDraft(selected);
      setQuery("");
    }
  }, [selected, open]);

  // Close on outside click (no popover primitive in this repo).
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  function toggle(v: string) {
    setDraft((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]));
  }
  function apply() {
    onApply(draft);
    setOpen(false);
  }

  const count = selected.length;
  const visible =
    searchable && query.trim()
      ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
      : options;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
        className="iris-input flex w-full items-center justify-between gap-2 text-left text-sm"
      >
        <span>
          {label}
          {count > 0 ? <span className="text-[var(--iris-fg-3)]"> ({count})</span> : null}
        </span>
        <span aria-hidden className="text-[var(--iris-fg-4)]">
          ▾
        </span>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-full min-w-[13rem] rounded-md border border-[var(--iris-bg-4)] bg-[var(--iris-bg-1)] p-2 shadow-lg">
          {searchable && (
            <input
              type="text"
              aria-label={`Search ${label}`}
              placeholder="Search…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="iris-input mb-2 w-full text-sm"
            />
          )}
          <div className="max-h-56 overflow-auto">
            {visible.length === 0 && (
              <div className="px-1.5 py-1 text-sm text-[var(--iris-fg-3)]">No matches</div>
            )}
            {visible.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-[var(--iris-bg-3)]"
              >
                <input
                  type="checkbox"
                  checked={draft.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  aria-label={o.label}
                />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between border-t border-[var(--iris-bg-4)] pt-2">
            <button
              type="button"
              className="text-xs text-[var(--iris-fg-3)] hover:text-[var(--iris-fg-1)]"
              onClick={() => setDraft([])}
            >
              Clear
            </button>
            <button type="button" className="iris-btn iris-btn--primary text-xs" onClick={apply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
